import {
  FieldTypes,
  IField,
} from "@/api/AppApi/services/builder/interfaces/interfaces";
import { FieldValues, UseFormReturn } from "react-hook-form";
import MapLocation from "./common-fields/location/MapLocation";
import TextAreaHook from "./common-fields/HookTextArea";
import CustomDropdown from "./common-fields/hook-dropdown/CustomDropdown";
import CommonDateTimeInput from "./common-fields/hooks-date-time/CommonDateTimeInput";
import HookRadioButtons from "./common-fields/hook-checkbox-radio/HookRadioButtons";
import HookOptionDropdown from "./common-fields/hook-options-dropdown/HookOptionDropdown";
import HookUpload from "./common-fields/hook-upload-files/HookUpload";
import HookPhoneNumber from "./common-fields/hook-phone-number/HookPhoneNumber";
import HookInput from "./common-fields/hook-input/HookInput";
import AddressTypeBtns from "./common-fields/hook-address-type/AddressTypeBtns";
import HookSelectOneDropdown from "./common-fields/hook-select-dropdown/HookSelectOneDropdown";
import HookNumber from "./common-fields/HookNumber";
import { useTranslation } from "@/hooks/localization/useTranslations";
import HookInputMultiLang from "./common-fields/hook-input-lang/HookInput";

interface Props {
  field: IField;
  form: UseFormReturn<FieldValues, any>;
  isSearchDropDown?: boolean;
  isGetKeyValue?: boolean;
  customOnChange?: (value: any) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
}

const CommonFormBuilder: React.FC<Props> = ({
  form,
  field,
  isSearchDropDown = false,
  isGetKeyValue,
  customOnChange,
  disabled = false,
  className,
  id,
}) => {
  const t = useTranslation();
  const fConfig = field?.configurations;
  const getField = () => {
    switch (fConfig?.type) {
      case FieldTypes.address_types:
        return <AddressTypeBtns form={form} field={field} />;
      case FieldTypes.phone:
      case FieldTypes.mobile:
        return <HookPhoneNumber field={field} form={form} />;
      case FieldTypes.map:
        return <MapLocation field={field} form={form} />;
      case FieldTypes.number:
        return <HookNumber field={field} form={form} />;
      case FieldTypes.textarea:
        return <TextAreaHook field={field} form={form} />;
      case FieldTypes.text:
        return (
          <HookInputMultiLang
            name={fConfig.key}
            control={form.control}
            label={fConfig.title}
            required={fConfig?.is_required}
            type={"text"}
            rules={{
              required: fConfig.is_required ? t.this_field_is_required : "",
            }}
            placeholder={fConfig?.placeholder}
            hideLabel={fConfig?.hideLabel}
            hint={fConfig.hint}
            errorMsg={form.formState.errors[fConfig.key]?.message as string}
            multiLang={fConfig.multi_lang}
            disabled={disabled}
          />
        );
      case FieldTypes.password:
      case FieldTypes.email:
        return (
          <HookInput
            label={fConfig.title}
            required={fConfig?.is_required}
            type={fConfig.type === FieldTypes.password ? "password" : "text"}
            inputRegister={{
              ...form.register(fConfig.key, {
                required: fConfig.is_required ? t.this_field_is_required : "",
              }),
            }}
            placeholder={fConfig?.placeholder}
            hideLabel={fConfig?.hideLabel}
            hint={fConfig.hint}
            errorMsg={form.formState.errors[fConfig.key]?.message as string}
          />
        );
      case FieldTypes.date:
      case FieldTypes.time:
      case FieldTypes.date_time:
        return <CommonDateTimeInput form={form} field={field} />;
      case FieldTypes.radio_buttons:
      case FieldTypes.checkbox:
        return <HookRadioButtons field={field} form={form} />;
      case FieldTypes.select:
        return (
          <CustomDropdown
            {...field}
            form={form}
            isSearch={isSearchDropDown}
            isGetKeyValue={isGetKeyValue}
            customOnChange={customOnChange}
            className={className}
            id={id}
          />
        );
      case FieldTypes.multi_select:
        return <HookOptionDropdown field={field} form={form} />;
      case FieldTypes.select_one_dropdown:
        return <HookSelectOneDropdown field={field} form={form} />;
      case FieldTypes.file:
      case FieldTypes.multi_file:
        return <HookUpload field={field} form={form} />;
      default:
        return null;
    }
  };
  return (
    <div className={`form-field ${className ?? ""}`} id={id}>
      {fConfig?.description && (
        <div className="field-description">{fConfig.description}</div>
      )}
      {getField()}
    </div>
  );
};

export default CommonFormBuilder;
